import { FileText, Loader2, Trash2, CheckCircle2, Info, ChevronDown, History, RefreshCw, Layers } from 'lucide-react';
import { useEffect, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DocumentDto, DocumentLogEntry, DocumentStatus } from '@/types/document';
import { isTerminalDocumentStatus } from '@/types/document';
import { useApiToken } from '@/auth/hooks';
import { formatBytes } from '@/lib/utils/formatBytes';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { pluginRegistry } from '@/plugins/pluginRegistry';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useDocumentHub } from '@/hooks/useDocumentHub';
import { useDocumentStore } from '@/stores/useDocumentStore';
import { useOrgMemberStore } from '@/stores/useOrgMemberStore';

// ── Ingest Flow Visualization ─────────────────────────────────────────────────

interface FlowStep {
  id: string;
  label: string;
  state: 'completed' | 'active' | 'pending';
}

interface ProcessingLogChapter {
  id: string;
  label: string;
  badgeClassName: string;
  logs: DocumentLogEntry[];
  title?: string | null;
}

function parseMachineModel(logs: DocumentLogEntry[]): string | null {
  for (const l of logs) {
    // "Triage-Maschinenmodell übernommen: 'C 40 S' (Konfidenz: ...)"
    const m = l.message.match(/Triage-Maschinenmodell übernommen:\s*'(.+?)'/i)
      ?? l.message.match(/Maschinenmodell im Graphen verankert:\s*(.+)/i);
    if (m) return m[1].trim();
  }
  return null;
}

function isMachineModelLog(log: DocumentLogEntry): boolean {
  return /Triage-Maschinenmodell übernommen:\s*'(.+?)'/i.test(log.message)
    || /Maschinenmodell im Graphen verankert:\s*(.+)/i.test(log.message);
}

function isRouteLog(log: DocumentLogEntry): boolean {
  return log.message.startsWith('Ingest-Route gewählt:')
    || /routes=(technical|interview_report|generic)/i.test(log.message);
}

function getRoutePresentation(routeType: string | null): {
  label: string | null;
  className: string;
} {
  const label =
    routeType === 'technical' ? 'Multimodal'
    : routeType === 'interview_report' ? 'Interview'
    : routeType === 'generic' ? 'Text'
    : routeType;

  const className =
    routeType === 'technical'
      ? 'bg-violet-500/10 text-violet-600 border-violet-500/20'
      : routeType === 'interview_report'
      ? 'bg-amber-500/10 text-amber-600 border-amber-500/20'
      : 'bg-sky-500/10 text-sky-600 border-sky-500/20';

  return { label, className };
}

function getFlowStepBadgeClassName(step: FlowStep): string {
  if (step.state === 'completed') {
    return 'bg-green-500/10 text-green-700 border-green-500/20';
  }
  if (step.state === 'active') {
    return 'bg-blue-500/10 text-blue-600 border-blue-500/20 animate-pulse';
  }
  return 'bg-muted/50 text-muted-foreground border-border/40';
}

function isPhaseLog(stepId: FlowStep['id'], log: DocumentLogEntry): boolean {
  const stage = log.stage?.toLowerCase();

  switch (stepId) {
    case 'parsing':
      return stage === 'parsing'
        || /document parsing started|preparing document for python processing|Extracted \d+|parsed \d+|Parsing.*complete/i.test(log.message);
    case 'chunking':
      return stage === 'chunking'
        || /(?:Split into|Created) \d+ chunks|Multimodal chunks:|chunking/i.test(log.message);
    case 'vision':
      return stage === 'vision'
        || /Vision.*(?:captioning|Batch|enrichment)|Enriched.*image chunks|ocr|Mistral/i.test(log.message);
    case 'extraction':
      return stage === 'extracting'
        || /extraction:|starting extraction|entity-extraction|processing entities|Extraktion/i.test(log.message);
    case 'graph':
      return stage === 'graph'
        || /Freigabe abgeschlossen|Entitäten.*Beziehungen|Graphen verankert|graph/i.test(log.message);
    case 'embedding':
      return stage === 'embedding'
        || /Vektorindex|embedding/i.test(log.message);
    default:
      return false;
  }
}

export function buildFlowSteps(
  logs: DocumentLogEntry[],
  currentStage: string | undefined,
  status: DocumentStatus,
  seenStages?: string[],
): { steps: FlowStep[]; routeType: string | null; routeReason: string | null; machineModel: string | null } {
  const routeLog = logs.find(l => l.message.startsWith('Ingest-Route gewählt:'));
  let routeType: string | null = null;
  let routeReason: string | null = null;
  if (routeLog) {
    const m = routeLog.message.match(/Ingest-Route gewählt: (\S+)\s+\((.+)\)/);
    if (m) { routeType = m[1]; routeReason = m[2]; }
  }
  // Fallback: aus Telemetry-Log (z.B. "telemetry ingest.chunking routes=technical ...")
  if (!routeType) {
    const telemetryLog = logs.find(l => /routes=(technical|interview_report|generic)/.test(l.message));
    if (telemetryLog) {
      const m = telemetryLog.message.match(/routes=(\S+)/);
      if (m) routeType = m[1];
    }
  }

  const logHas = (pattern: RegExp) => logs.some(l => pattern.test(l.message));
  const stageIn = (s: string) =>
    logs.some(l => l.stage === s) ||
    currentStage === s ||
    (seenStages?.includes(s) ?? false);
  const isCompleted = status === 'Completed' || status === 'PartiallyFailed' || status === 'Failed';

  const hasParsing = stageIn('Parsing') || logHas(/Extracted \d+|parsed \d+|Parsing.*complete/i);
  const hasChunking = stageIn('Chunking') || logHas(/(?:Split into|Created) \d+ chunks|Multimodal chunks:/i);
  const hasVision = stageIn('Vision') || logHas(/Vision.*(?:captioning|Batch|enrichment)|Enriched.*image chunks/i);
  const hasExtraction = stageIn('Extracting') || status === 'Extracting' || logHas(/extraction:|starting extraction|entity-extraction/i);
  const hasGraph = logHas(/Freigabe abgeschlossen|Entitäten.*Beziehungen/);
  // Robuste Erkennung analog zu den übrigen Stages (mehrere Patterns + stageIn +
  // Completed-Fallback): vorher hing der Badge allein an "/Vektorindex/" — fehlte
  // diese eine (Truncation-anfällige) Zeile, verschwand der Badge auch bei
  // vollständig embeddeten, abgeschlossenen Dokumenten (z. B. 9633DE).
  const hasEmbedding =
    logHas(/Vektorindex|berechne Embeddings|Embeddings gespeichert/) ||
    stageIn('Embedding') ||
    (status === 'Completed' && hasGraph);

  const steps: FlowStep[] = [];

  if (hasParsing) {
    const parsingDone = hasChunking || hasVision || hasExtraction || hasGraph || hasEmbedding || isCompleted;
    steps.push({ id: 'parsing', label: 'Parsing',
      state: currentStage === 'Parsing' && !parsingDone ? 'active' : 'completed' });
  }
  if (hasChunking) {
    const chunkingDone = hasVision || hasExtraction || hasGraph || hasEmbedding || isCompleted;
    steps.push({ id: 'chunking', label: 'Chunking',
      state: currentStage === 'Chunking' && !chunkingDone ? 'active' : 'completed' });
  }
  if (hasVision) {
    const visionDone = hasExtraction || hasGraph || hasEmbedding || isCompleted;
    steps.push({ id: 'vision', label: 'Vision',
      state: currentStage === 'Vision' && !visionDone ? 'active' : 'completed' });
  }
  if (hasExtraction) {
    steps.push({ id: 'extraction', label: 'Extraktion',
      state: status === 'Extracting' && !hasGraph && !isCompleted ? 'active' : 'completed' });
  }
  if (hasGraph) {
    steps.push({ id: 'graph', label: 'Graph', state: 'completed' });
  }
  if (hasEmbedding) {
    const embeddingDone = logHas(/Vektorindex|Embeddings gespeichert/) || status === 'Completed';
    steps.push({ id: 'embedding', label: 'Embedding',
      state: currentStage === 'Embedding' && !embeddingDone ? 'active' : 'completed' });
  }

  return { steps, routeType, routeReason, machineModel: parseMachineModel(logs) };
}

function buildProcessingLogChapters(
  logs: DocumentLogEntry[],
  currentStage: string | undefined,
  status: DocumentStatus,
  seenStages?: string[],
): { chapters: ProcessingLogChapter[]; overflowLogs: DocumentLogEntry[] } {
  const { steps, routeType, routeReason, machineModel } = buildFlowSteps(logs, currentStage, status, seenStages);
  const chapters: ProcessingLogChapter[] = [];
  const usedLogIndexes = new Set<number>();
  const machineBadgeClassName = 'bg-sopho-deep/10 text-sopho-entity border-sopho-entity/20';

  const collectLogs = (matcher: (log: DocumentLogEntry) => boolean): DocumentLogEntry[] => {
    const matches: DocumentLogEntry[] = [];
    logs.forEach((log, index) => {
      if (usedLogIndexes.has(index) || !matcher(log)) {
        return;
      }
      usedLogIndexes.add(index);
      matches.push(log);
    });
    return matches;
  };

  if (machineModel) {
    chapters.push({
      id: 'machine',
      label: machineModel,
      badgeClassName: machineBadgeClassName,
      logs: collectLogs(isMachineModelLog),
    });
  }

  if (routeType) {
    const routePresentation = getRoutePresentation(routeType);
    if (routePresentation.label) {
      chapters.push({
        id: 'route',
        label: routePresentation.label,
        badgeClassName: routePresentation.className,
        logs: collectLogs(isRouteLog),
        title: routeReason,
      });
    }
  }

  steps.forEach((step) => {
    chapters.push({
      id: step.id,
      label: step.label,
      badgeClassName: getFlowStepBadgeClassName(step),
      logs: collectLogs((log) => isPhaseLog(step.id, log)),
    });
  });

  const overflowLogs = logs.filter((_, index) => !usedLogIndexes.has(index));
  return { chapters, overflowLogs };
}

function parseBatchProgress(logs: DocumentLogEntry[]) {
  for (let i = logs.length - 1; i >= 0; i--) {
    const m = logs[i].message.match(/Batch (\d+)\/(\d+) extraction: (\d+)\/(\d+)/);
    if (m) return { batchNum: +m[1], batchTotal: +m[2], done: +m[3], total: +m[4] };
  }
  return null;
}

function IngestFlowBadges({ logs, currentStage, status, seenStages }: {
  logs: DocumentLogEntry[];
  currentStage?: string;
  status: DocumentStatus;
  seenStages?: string[];
}) {
  const { steps, routeType, routeReason, machineModel } = buildFlowSteps(logs, currentStage, status, seenStages);

  if (!routeType && steps.length === 0 && !machineModel) return null;

  const routePresentation = getRoutePresentation(routeType);

  return (
    <div className="mt-1.5 flex items-center gap-1 flex-wrap">
      {machineModel && (
        <span className="text-[8px] px-1.5 py-0.5 rounded border font-semibold bg-sopho-deep/10 text-sopho-entity border-sopho-entity/20">
          {machineModel}
        </span>
      )}
      {routeType && (
        <span
          className={cn('text-[8px] px-1.5 py-0.5 rounded border font-semibold', routePresentation.className)}
          title={routeReason ?? undefined}
        >
          {routePresentation.label}
        </span>
      )}
      {steps.map((step) => (
        <span key={step.id} className="flex items-center gap-1">
          <span className="text-[7px] text-muted-foreground/40">→</span>
          <span className={cn(
            'text-[8px] px-1.5 py-0.5 rounded border font-medium',
            getFlowStepBadgeClassName(step),
          )}>
            {step.state === 'completed' && '✓ '}{step.label}
          </span>
        </span>
      ))}
    </div>
  );
}

interface DocumentListItemProps {
  document: DocumentDto;
  onDelete: (id: string) => void;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
}

function getStatusConfig(status: DocumentStatus): {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  className?: string;
} {
  switch (status) {
    case 'Uploaded':
      return { label: 'Hochgeladen', variant: 'secondary' };
    case 'AwaitingConfirmation':
      return {
        label: 'Analyse…',
        variant: 'default',
        className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
      };
    case 'Parsing':
    case 'Extracting':
      return {
        label: status === 'Parsing' ? 'Analysiere…' : 'Extrahiere…',
        variant: 'default',
        className: 'bg-blue-500/10 text-blue-600 border-blue-500/20'
      };
    case 'Parsed':
      return { label: 'Analysiert', variant: 'outline' };
    case 'Completed':
      return { 
        label: 'Abgeschlossen', 
        variant: 'default',
        className: 'bg-green-500/10 text-green-600 border-green-500/20'
      };
    case 'PartiallyFailed':
      return { 
        label: 'Teilweise fehlgeschlagen', 
        variant: 'default',
        className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20'
      };
    case 'Failed':
      return { 
        label: 'Fehlgeschlagen', 
        variant: 'destructive'
      };
    default:
      return { label: status, variant: 'outline' };
  }
}

const isProcessing = (status: DocumentStatus): boolean => {
  return status === 'Parsing' || status === 'Extracting' || status === 'AwaitingConfirmation';
};

/** Keep SignalR connected for any in-flight pipeline state (including Parsed,
 *  because the server auto-triggers extraction immediately after Parsed). */
const needsHubConnection = (status: DocumentStatus): boolean => {
  return status === 'Parsing' || status === 'Parsed' || status === 'Extracting' || status === 'AwaitingConfirmation';
};

export function DocumentListItem({ document, onDelete, isSelected = false, onSelect }: DocumentListItemProps) {
  const processing = isProcessing(document.status);
  const isCompleted = document.status === 'Completed';
  const isPartiallyFailed = document.status === 'PartiallyFailed';

  const { currentUser } = useOrgMemberStore();
  const isAdmin = currentUser?.role === 'Admin' || currentUser?.role === 'SuperAdmin';
  const navigate = useNavigate();

  // Get processing details from document store (populated by SignalR)
  const processingDetails = useDocumentStore((state) => state.getProcessingDetails(document.id));
  const updateDocumentProgress = useDocumentStore((state) => state.updateDocumentProgress);
  const applyDocumentSnapshot = useDocumentStore((state) => state.applyDocumentSnapshot);
  const addDocumentLog = useDocumentStore((state) => state.addDocumentLog);
  const addDocumentError = useDocumentStore((state) => state.addDocumentError);
  const fetchProcessingLogs = useDocumentStore((state) => state.fetchProcessingLogs);
  const retryDocument = useDocumentStore((state) => state.retryDocument);
  const forceCompleteDocument = useDocumentStore((state) => state.forceCompleteDocument);
  const { getAccessToken } = useApiToken();

  const [isRetrying, setIsRetrying] = useState(false);
  const [showForceCompleteDialog, setShowForceCompleteDialog] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    const token = await getAccessToken().catch(() => null);
    if (!token) {
      toast.error('Authentifizierung erforderlich');
      setIsRetrying(false);
      return;
    }
    const ok = await retryDocument(document.id, token);
    if (!ok) toast.error('Retry fehlgeschlagen');
    else toast.success('Retry gestartet');
    setIsRetrying(false);
  };

  const handleForceComplete = async () => {
    setShowForceCompleteDialog(false);
    const token = await getAccessToken().catch(() => null);
    if (!token) {
      toast.error('Authentifizierung erforderlich');
      return;
    }
    const ok = await forceCompleteDocument(document.id, token);
    if (!ok) toast.error('Konnte Status nicht ändern');
  };

  // Fetch persisted processing logs from server on mount
  // Only for documents that have been processed (not freshly uploaded)
  useEffect(() => {
    if (document.status === 'Uploaded') return;
    getAccessToken().then((token) => {
      if (token) fetchProcessingLogs(document.id, token);
    });
  }, [document.id, document.status, fetchProcessingLogs, getAccessToken]);
  
  // Connect to SignalR hub while document is in any pipeline-active state
  useDocumentHub(
    needsHubConnection(document.status) ? document.id : null,
    {
      onProgressUpdate: (data) => {
        updateDocumentProgress(data);
      },
      onStatusChanged: (data) => {
        applyDocumentSnapshot(data);
      },
      onLogEntry: (data) => {
        addDocumentLog(data);
      },
      onError: (data) => {
        addDocumentError(data.documentId, data.error);
      },
    }
  );

  const statusConfig = getStatusConfig(document.status);
  const prefersPersistedCounters = isTerminalDocumentStatus(document.status);
  const visibleCurrent = prefersPersistedCounters
    ? document.processedChunks
    : processingDetails?.current ?? document.processedChunks;
  const visibleTotal = prefersPersistedCounters
    ? document.totalChunks
    : processingDetails?.total ?? document.totalChunks;
  const showProgress = visibleTotal > 0;
  const progressPercent = visibleTotal > 0
    ? Math.round((visibleCurrent / visibleTotal) * 100)
    : 0;

  const logs = processingDetails?.logs || [];
  const processingLogChapters = buildProcessingLogChapters(
    logs,
    processingDetails?.stage,
    document.status,
    processingDetails?.seenStages,
  );

  // Batch extraction progress (parsed from log messages)
  const batchProgress = document.status === 'Extracting' ? parseBatchProgress(logs) : null;

  // Never show 100% while the pipeline is still active — only Completed = done.
  const isActivelyProcessing = document.status === 'Parsing' || document.status === 'Parsed' || document.status === 'Extracting';
  const effectiveProgress = isActivelyProcessing && progressPercent >= 100 ? 97 : progressPercent;

  const handleSelect = () => {
    onSelect?.(document.id);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onSelect) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleSelect();
  };

  return (
    <TooltipProvider>
      <div className={cn(
        "group flex flex-col gap-1.5 rounded-md border border-border/60 bg-card/40 px-2 py-2 transition hover:bg-card/60",
        onSelect && "cursor-pointer",
        isSelected && "border-primary/50 bg-primary/5 ring-1 ring-primary/20",
        !document.isCurrent && "opacity-50"
      )}
        role={onSelect ? 'button' : undefined}
        tabIndex={onSelect ? 0 : undefined}
        aria-pressed={onSelect ? isSelected : undefined}
        onClick={handleSelect}
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-start gap-2">
          <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
            {processing ? (
              <Loader2 className="h-3 w-3 animate-spin text-primary" />
            ) : isCompleted ? (
              <CheckCircle2 className="h-3 w-3 text-green-600" />
            ) : (
              <FileText className="h-3 w-3" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[10px]">
              <div className="truncate text-[10px] font-medium text-foreground" title={document.fileName}>
                {document.fileName}
              </div>
              {document.versionNumber > 1 && (
                <Badge
                  variant="outline"
                  className="text-[8px] px-1 py-0 font-mono border-violet-500/30 text-violet-600"
                  title={document.versionLabel || `Version ${document.versionNumber}`}
                >
                  <History className="mr-0.5 h-2 w-2" />
                  v{document.versionNumber}
                </Badge>
              )}
              {!document.isCurrent && (
                <Badge
                  variant="outline"
                  className="text-[8px] px-1 py-0 border-amber-500/30 text-amber-600"
                >
                  Ersetzt
                </Badge>
              )}
              <Badge 
                variant={statusConfig.variant}
                className={cn(
                  "text-[9px] px-1.5 py-0",
                  statusConfig.className,
                  processing && "animate-pulse"
                )}
              >
                {statusConfig.label}
              </Badge>
              {processingDetails?.stage && processing && (
                <span className="text-[8px] text-muted-foreground">
                  {processingDetails.stage}
                </span>
              )}
              {isPartiallyFailed && (
                <>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      void handleRetry();
                    }}
                    disabled={isRetrying}
                    className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-[9px] font-medium text-primary hover:bg-primary/15 disabled:opacity-50 transition-colors"
                  >
                    {isRetrying ? (
                      <Loader2 className="h-2.5 w-2.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-2.5 w-2.5" />
                    )}
                    Erneut versuchen
                  </button>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowForceCompleteDialog(true);
                    }}
                    className="inline-flex items-center rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-[9px] font-medium text-primary hover:bg-primary/15 transition-colors"
                  >
                    Abschließen
                  </button>
                </>
              )}
            </div>

          <div className="mt-0.5 flex items-center gap-1.5">
            <span className="text-[9px] text-muted-foreground">
              {formatBytes(document.fileSizeBytes)}
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Info className="h-3 w-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-[10px] max-w-xs">
                <div className="space-y-1">
                  <div><span className="font-semibold">Dokument ID:</span> {document.id}</div>
                  <div><span className="font-semibold">Workspace:</span> {document.workspaceId}</div>
                  <div><span className="font-semibold">Version:</span> {document.versionNumber}{document.versionLabel ? ` (${document.versionLabel})` : ''}{document.isCurrent ? ' — Aktuell' : ' — Ersetzt'}</div>
                  <div><span className="font-semibold">Erstellt:</span> {new Date(document.createdAt).toLocaleString('de-DE')}</div>
                  {document.completedAt && (
                    <div><span className="font-semibold">Abgeschlossen:</span> {new Date(document.completedAt).toLocaleString('de-DE')}</div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </div>

          {showProgress && (
            <div className="mt-1.5 space-y-1">
              <div className="flex items-center gap-2">
                <Progress value={effectiveProgress} className="h-1.5 flex-1" />
                <span className="text-[9px] font-medium text-muted-foreground shrink-0">
                  {batchProgress
                    ? `Batch ${batchProgress.batchNum}/${batchProgress.batchTotal}`
                    : `${visibleCurrent}/${visibleTotal}`}
                  {isPartiallyFailed && document.failedChunks > 0 && (
                    <span className="text-destructive ml-1">
                      · {document.failedChunks} fehlgeschlagen
                    </span>
                  )}
                </span>
              </div>
              {batchProgress && (
                <div className="text-[8px] text-muted-foreground font-mono">
                  LLM-Extraktion: {batchProgress.done}/{batchProgress.total} ({Math.round(batchProgress.done / batchProgress.total * 100)}%)
                </div>
              )}
            </div>
          )}

          {/* Ingest Flow Visualization */}
          {logs.length > 0 && (
            <IngestFlowBadges
              logs={logs}
              currentStage={processingDetails?.stage}
              status={document.status}
              seenStages={processingDetails?.seenStages}
            />
          )}

          {document.errorMessage && (
            <div className="mt-1 text-[9px] text-destructive">
              {document.errorMessage}
            </div>
          )}

          <AlertDialog open={showForceCompleteDialog} onOpenChange={setShowForceCompleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Dokument als abgeschlossen markieren?</AlertDialogTitle>
                <AlertDialogDescription>
                  {document.failedChunks} Chunk{document.failedChunks !== 1 ? 's' : ''} konnten nicht
                  verarbeitet werden. Diese Daten fehlen im Wissensgraph.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={handleForceComplete}>
                  Als abgeschlossen markieren
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Processing Logs Collapsible */}
          {logs.length > 0 && (
            <div className="mt-2">
              <Collapsible className="border border-border/40 rounded-md overflow-hidden">
                <CollapsibleTrigger
                  className="w-full flex items-center justify-between px-2 py-1.5 text-left bg-muted/30 hover:bg-muted/50 transition-colors [&[data-state=open]>svg]:rotate-180"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSelect();
                  }}
                >
                  <span className="text-[9px] font-medium text-muted-foreground">Verarbeitungsprotokoll ({logs.length})</span>
                  <ChevronDown className="h-3 w-3 text-muted-foreground transition-transform duration-200" />
                </CollapsibleTrigger>
                <CollapsibleContent className="px-2 py-1.5 bg-card/20">
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {processingLogChapters.chapters.map((chapter) => (
                      <div key={chapter.id} className="space-y-0.5" data-testid={`processing-log-chapter-${chapter.id}`}>
                        <div className="flex items-center justify-between gap-2 px-1 py-0.5 border-b border-border/40">
                          <span
                            className={cn('text-[8px] px-1.5 py-0.5 rounded border font-semibold', chapter.badgeClassName)}
                            title={chapter.title ?? undefined}
                          >
                            {chapter.label}
                          </span>
                          <span className="text-[7px] uppercase tracking-[0.12em] text-muted-foreground">
                            {chapter.logs.length} Einträge
                          </span>
                        </div>
                        {chapter.logs.length > 0 ? chapter.logs.map((log, idx) => (
                          <div
                            key={`${chapter.id}-${idx}-${log.timestamp}`}
                            className={cn(
                              'text-[8px] font-mono px-1.5 py-0.5 rounded ml-2',
                              log.level === 'error' && 'text-red-600 bg-red-50/50',
                              log.level === 'warning' && 'text-yellow-600 bg-yellow-50/50',
                              log.level === 'info' && 'text-blue-600 bg-blue-50/50'
                            )}
                          >
                            <span className="font-semibold">[{new Date(log.timestamp).toLocaleTimeString('de-DE')}]</span> {log.message}
                          </div>
                        )) : (
                          <div className="text-[8px] px-1.5 py-0.5 rounded ml-2 text-muted-foreground bg-muted/30">
                            Noch keine Protokolleinträge.
                          </div>
                        )}
                      </div>
                    ))}
                    {processingLogChapters.overflowLogs.length > 0 && (
                      <div className="space-y-0.5" data-testid="processing-log-chapter-overflow">
                        <div className="flex items-center justify-between gap-2 px-1 py-0.5 border-b border-border/40">
                          <span className="text-[8px] px-1.5 py-0.5 rounded border font-semibold bg-muted/50 text-muted-foreground border-border/40">
                            Weitere Ereignisse
                          </span>
                          <span className="text-[7px] uppercase tracking-[0.12em] text-muted-foreground">
                            {processingLogChapters.overflowLogs.length} Einträge
                          </span>
                        </div>
                        {processingLogChapters.overflowLogs.map((log, idx) => (
                          <div
                            key={`overflow-${idx}-${log.timestamp}`}
                            className={cn(
                              'text-[8px] font-mono px-1.5 py-0.5 rounded ml-2',
                              log.level === 'error' && 'text-red-600 bg-red-50/50',
                              log.level === 'warning' && 'text-yellow-600 bg-yellow-50/50',
                              log.level === 'info' && 'text-blue-600 bg-blue-50/50'
                            )}
                          >
                            <span className="font-semibold">[{new Date(log.timestamp).toLocaleTimeString('de-DE')}]</span> {log.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}
          </div>

          <div className="flex items-center gap-1">
            {isAdmin && isCompleted && pluginRegistry.isPluginEnabled('chunk-visualizer') && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/app/admin/chunk-visualizer?docId=${document.id}`);
                }}
                className="rounded p-0.5 text-muted-foreground/0 transition group-hover:text-muted-foreground hover:text-primary"
                title="Chunk-Ansicht"
              >
                <Layers className="h-3 w-3" />
              </button>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(document.id);
              }}
              className="rounded p-0.5 text-muted-foreground/0 transition group-hover:text-muted-foreground hover:text-destructive"
              title="Dokument löschen"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
